import { ArrowBackIcon, ExternalLinkIcon } from "@chakra-ui/icons"
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

import { OpenAPI } from "../../../client"

export const Route = createFileRoute("/_layout/admin/customers/$customerId")({
  component: CustomerDetailsPage,
})

type CustomerItem = {
  id: number
  customer_id: string
  code: string
  name: string
  email: string
  phone: string
  country_code: string
  status: string
  created_at?: string
  updated_at?: string
}

type CustomerUpsertPayload = {
  customer_id: string
  code: string
  name: string
  email: string
  phone: string
  country_code: string
  status: string
}

type LicenseItem = {
  id: number
  customer_id: string
  customer_code: string
  license_id: string
  product: RuntimeTarget
  plan: string
  status: string
  starts_at: string
  expires_at: string
  grace_days: number
  offline_max_days: number
  critical_after_limited_days: number
  capability_enforcement_mode: "enforce" | "audit" | string
  emergency_finalize_enabled: boolean
  license_revision: number
  contract_version: number
  module_overrides: Record<string, boolean>
  features: Record<string, boolean>
  limits: Record<string, number>
  updated_at: string
}

type LicenseListResponse = {
  items: LicenseItem[]
  total: number
  limit: number
  offset: number
}

type UpsertPayload = {
  customer_id: string
  customer_code: string
  license_id: string
  product: RuntimeTarget
  plan: string
  status: string
  starts_at: string
  expires_at: string
  grace_days: number
  offline_max_days: number
  critical_after_limited_days: number
  capability_enforcement_mode: "enforce" | "audit" | string
  emergency_finalize_enabled: boolean
  contract_version?: number
  module_overrides?: Record<string, boolean>
  expected_revision?: number
  features: Record<string, boolean>
  limits: Record<string, number>
}

type RuntimeTarget = "wms" | "hrm"

type FeatureDefinition = {
  key: string
  label: string
  description?: string
  basic_included?: boolean
  pro_included?: boolean
  dependencies?: string[]
  editable?: boolean
}

type LimitDefinition = {
  key: string
  label: string
  description?: string
  default_value: number
  min_value: number
}

type ProductContract = {
  product: RuntimeTarget
  contract_version?: number
  plans?: Record<string, { label: string; included: Record<string, boolean> }>
  modules?: Array<{
    key: string
    label: string
    scope: string
    dependencies: string[]
    editable: boolean
  }>
  default_features: Record<string, boolean>
  default_limits: Record<string, number>
  feature_definitions: FeatureDefinition[]
  limit_definitions: LimitDefinition[]
}

type LicenseOptionsResponse = {
  statuses: string[]
  products: Record<RuntimeTarget, ProductContract>
}

type GUSIntegrationStatus = {
  source: string
  status: string
  provider_mode: string
  mocked: boolean
  can_lookup: boolean
  api_key_configured: boolean
  public_lookup_path: string
  message: string
  todo?: string[]
  checked_at: string
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("access_token") || ""
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(
        parsed?.error ||
          parsed?.detail ||
          parsed?.message ||
          `HTTP ${res.status}`,
      )
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  return (await res.json()) as T
}

function cloneBooleanMap(input?: Record<string, boolean>) {
  return { ...(input ?? {}) }
}

function cloneNumberMap(input?: Record<string, number>) {
  return { ...(input ?? {}) }
}

function toDateInputValue(value?: string) {
  if (!value) return ""
  const date = new Date(value)
  if (!Number.isFinite(date.getTime())) return ""
  return date.toISOString().slice(0, 10)
}

function normalizeProductContract(
  product: RuntimeTarget,
  contract?: Partial<ProductContract> | null,
): ProductContract {
  return {
    product,
    contract_version: contract?.contract_version,
    plans: contract?.plans,
    modules: contract?.modules,
    default_features: cloneBooleanMap(contract?.default_features),
    default_limits: cloneNumberMap(contract?.default_limits),
    feature_definitions: Array.isArray(contract?.feature_definitions)
      ? contract.feature_definitions
      : [],
    limit_definitions: Array.isArray(contract?.limit_definitions)
      ? contract.limit_definitions
      : [],
  }
}

function serializeLicensePayload(
  payload: UpsertPayload,
  contract?: ProductContract,
) {
  if (payload.product !== "wms") return payload
  if (!contract?.modules?.length || !contract.plans?.[payload.plan]) {
    throw new Error(
      "Nie udało się wczytać aktualnego kontraktu modułów. Odśwież opcje licencji.",
    )
  }
  const moduleOverrides: Record<string, boolean> = {}
  for (const module of contract.modules) {
    if (payload.module_overrides?.[module.key] === false)
      moduleOverrides[module.key] = false
  }
  const { features: _legacyFeatures, ...base } = payload
  return {
    ...base,
    contract_version: contract.contract_version ?? 2,
    module_overrides: moduleOverrides,
  }
}

function buildFallbackOptions(): LicenseOptionsResponse {
  return {
    statuses: [
      "active",
      "expired_grace",
      "expired_limited",
      "expired_critical",
      "unlicensed",
      "suspended",
      "revoked",
    ],
    products: {
      wms: {
        product: "wms",
        default_features: {
          technology_management: true,
          inventory_count: true,
          advanced_reports: true,
          advanced_analytics: true,
        },
        default_limits: { exports_per_day: 100 },
        feature_definitions: [
          { key: "technology_management", label: "Technology Management" },
          { key: "inventory_count", label: "Inventory Count" },
          { key: "advanced_reports", label: "Advanced Reports" },
          { key: "advanced_analytics", label: "Advanced Analytics" },
        ],
        limit_definitions: [
          {
            key: "exports_per_day",
            label: "Exports per day",
            default_value: 100,
            min_value: 1,
          },
        ],
      },
      hrm: {
        product: "hrm",
        default_features: {},
        default_limits: {},
        feature_definitions: [],
        limit_definitions: [],
      },
    },
  }
}

function buildUpsertPayloadFromLicense(item: LicenseItem): UpsertPayload {
  return {
    customer_id: item.customer_id,
    customer_code: item.customer_code || "",
    license_id: item.license_id,
    product: item.product,
    plan: item.plan || "PRO",
    status: item.status || "active",
    starts_at: toDateInputValue(item.starts_at),
    expires_at: toDateInputValue(item.expires_at),
    grace_days: Number.isFinite(item.grace_days) ? item.grace_days : 14,
    offline_max_days: Number.isFinite(item.offline_max_days)
      ? item.offline_max_days
      : 14,
    critical_after_limited_days: Number.isFinite(
      item.critical_after_limited_days,
    )
      ? item.critical_after_limited_days
      : 30,
    capability_enforcement_mode:
      item.capability_enforcement_mode === "audit" ? "audit" : "enforce",
    emergency_finalize_enabled: Boolean(item.emergency_finalize_enabled),
    contract_version: item.contract_version ?? 1,
    module_overrides: cloneBooleanMap(item.module_overrides),
    expected_revision: item.license_revision,
    features: cloneBooleanMap(item.features),
    limits: cloneNumberMap(item.limits),
  }
}

function getCustomerStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "suspended":
      return "yellow"
    case "closed":
      return "red"
    default:
      return "gray"
  }
}

function getLicenseStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "expired_grace":
      return "yellow"
    case "expired_limited":
    case "suspended":
      return "orange"
    case "expired_critical":
    case "unlicensed":
    case "revoked":
      return "red"
    default:
      return "gray"
  }
}

function getGUSStatusColor(status?: string) {
  switch (status) {
    case "ready":
      return "green"
    case "mock":
      return "yellow"
    case "not_configured":
      return "orange"
    case "not_implemented":
    case "misconfigured":
      return "red"
    default:
      return "gray"
  }
}

function getGUSStatusLabel(status?: string) {
  switch (status) {
    case "ready":
      return "Gotowe"
    case "mock":
      return "Tryb mock"
    case "not_configured":
      return "Brak konfiguracji"
    case "not_implemented":
      return "TODO implementacji"
    case "misconfigured":
      return "Błędna konfiguracja"
    default:
      return "Nieznany status"
  }
}

function buildEmptyLicenseForm(
  customerId: string,
  customerCode: string,
  contract: ProductContract,
): UpsertPayload {
  return {
    customer_id: customerId,
    customer_code: customerCode,
    license_id: "",
    product: contract.product,
    plan: "PRO",
    status: "active",
    starts_at: new Date().toISOString().slice(0, 10),
    expires_at: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    grace_days: 14,
    offline_max_days: 14,
    critical_after_limited_days: 30,
    capability_enforcement_mode: "enforce",
    emergency_finalize_enabled: false,
    contract_version: 2,
    module_overrides: {},
    features: cloneBooleanMap(contract.default_features),
    limits: cloneNumberMap(contract.default_limits),
  }
}

function CustomerDetailsPage() {
  const { customerId } = Route.useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const revokeConfirm = useDisclosure()
  const panelBg = useColorModeValue("white", "gray.800")
  const mutedTextColor = useColorModeValue("gray.500", "gray.300")
  const tableHeadBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")
  const fallbackOptions = useMemo(() => buildFallbackOptions(), [])
  const [selectedLicenseId, setSelectedLicenseId] = useState("")
  const [renewDate, setRenewDate] = useState("")
  const [issuedToken, setIssuedToken] = useState<{
    token: string
    jti: string
    kid: string
    expiresAt: string
  } | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [customerValidationError, setCustomerValidationError] = useState<
    string | null
  >(null)
  const [customerForm, setCustomerForm] = useState<CustomerUpsertPayload>({
    customer_id: customerId,
    code: "",
    name: "",
    email: "",
    phone: "",
    country_code: "",
    status: "active",
  })
  const [form, setForm] = useState<UpsertPayload>(() =>
    buildEmptyLicenseForm(customerId, "", fallbackOptions.products.wms),
  )

  const customerQuery = useQuery({
    queryKey: ["lms", "customer", customerId],
    queryFn: () => apiFetch<CustomerItem>(`/v1/lms/customers/${customerId}`),
  })

  const optionsQuery = useQuery({
    queryKey: ["lms", "licenses", "options"],
    queryFn: () => apiFetch<LicenseOptionsResponse>("/v1/lms/licenses/options"),
  })

  const licensesQuery = useQuery({
    queryKey: ["lms", "licenses", "customer", customerId],
    queryFn: () =>
      apiFetch<LicenseListResponse>(
        `/v1/lms/licenses?limit=200&customer_id=${encodeURIComponent(customerId)}`,
      ),
  })

  const gusStatusQuery = useQuery({
    queryKey: ["lms", "gus", "status"],
    queryFn: () =>
      apiFetch<GUSIntegrationStatus>("/v1/lms/integrations/gus/status"),
  })

  const licenseOptions = optionsQuery.data ?? fallbackOptions
  const selectedLicense = useMemo(
    () =>
      (licensesQuery.data?.items ?? []).find(
        (item) => item.license_id === selectedLicenseId,
      ) ?? null,
    [licensesQuery.data?.items, selectedLicenseId],
  )

  const currentProductContract = useMemo(
    () =>
      normalizeProductContract(
        form.product,
        licenseOptions.products[form.product] ??
          fallbackOptions.products[form.product],
      ),
    [fallbackOptions.products, form.product, licenseOptions.products],
  )

  useEffect(() => {
    if (!customerQuery.data) return
    setCustomerForm({
      customer_id: customerQuery.data.customer_id,
      code: customerQuery.data.code || "",
      name: customerQuery.data.name || "",
      email: customerQuery.data.email || "",
      phone: customerQuery.data.phone || "",
      country_code: customerQuery.data.country_code || "",
      status: customerQuery.data.status || "active",
    })
    if (selectedLicenseId) return
    setForm((current) => ({
      ...current,
      customer_id: customerQuery.data.customer_id,
      customer_code: customerQuery.data.code || "",
    }))
  }, [customerQuery.data, selectedLicenseId])

  const customerUpdateMutation = useMutation({
    mutationFn: (payload: CustomerUpsertPayload) =>
      apiFetch<CustomerItem>("/v1/lms/customers", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: (data) => {
      setCustomerValidationError(null)
      setCustomerForm({
        customer_id: data.customer_id,
        code: data.code || "",
        name: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        country_code: data.country_code || "",
        status: data.status || "active",
      })
      toast({
        title: "Klient został zaktualizowany",
        status: "success",
        duration: 2500,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "customer", customerId],
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "customers"] })
      queryClient.invalidateQueries({
        queryKey: ["lms", "licenses", "customer", customerId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się zaktualizować klienta",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  useEffect(() => {
    if (!selectedLicense || selectedLicense.license_id === form.license_id)
      return
    setForm(buildUpsertPayloadFromLicense(selectedLicense))
    setRenewDate(toDateInputValue(selectedLicense.expires_at))
  }, [form.license_id, selectedLicense])

  const resetFormForNewLicense = (product: RuntimeTarget = "wms") => {
    const contract =
      licenseOptions.products[product] ?? fallbackOptions.products[product]
    setSelectedLicenseId("")
    setIssuedToken(null)
    setRenewDate("")
    setValidationError(null)
    setForm(
      buildEmptyLicenseForm(
        customerId,
        customerQuery.data?.code || "",
        normalizeProductContract(product, contract),
      ),
    )
  }

  const applyProductDefaults = (product: RuntimeTarget) => {
    const contract =
      licenseOptions.products[product] ?? fallbackOptions.products[product]
    setForm((current) => ({
      ...current,
      product,
      features: cloneBooleanMap(contract.default_features),
      limits: cloneNumberMap(contract.default_limits),
    }))
  }

  const upsertMutation = useMutation({
    mutationFn: (payload: UpsertPayload) =>
      apiFetch<LicenseItem>("/v1/lms/licenses", {
        method: "POST",
        body: JSON.stringify({
          ...serializeLicensePayload(
            payload,
            licenseOptions.products[payload.product],
          ),
          license_id: payload.license_id?.trim() || "",
          starts_at: `${payload.starts_at}T00:00:00Z`,
          expires_at: `${payload.expires_at}T00:00:00Z`,
        }),
      }),
    onSuccess: (data) => {
      setSelectedLicenseId(data.license_id)
      setIssuedToken(null)
      setValidationError(null)
      setForm(buildUpsertPayloadFromLicense(data))
      toast({
        title: "Licencja została zapisana",
        description: `${data.license_id}. Zapisano w LMS; zastosowanie w WMS nastąpi po synchronizacji.`,
        status: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "licenses", "customer", customerId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się zapisać",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const quickPlanMutation = useMutation({
    mutationFn: ({
      licenseId,
      plan,
      expectedRevision,
    }: {
      licenseId: string
      plan: "BASIC" | "PRO"
      expectedRevision: number
    }) =>
      apiFetch<LicenseItem>(`/v1/lms/licenses/${licenseId}/plan`, {
        method: "POST",
        body: JSON.stringify({ plan, expected_revision: expectedRevision }),
      }),
    onSuccess: (data) => {
      setSelectedLicenseId(data.license_id)
      setForm(buildUpsertPayloadFromLicense(data))
      toast({
        title: "Plan został zaktualizowany",
        description: `${data.license_id}: ${data.plan}. Zapisano w LMS; zastosowanie w WMS nastąpi po synchronizacji.`,
        status: "success",
        duration: 2500,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "licenses", "customer", customerId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się zaktualizować planu",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const renewMutation = useMutation({
    mutationFn: ({
      licenseId,
      expiresAt,
    }: {
      licenseId: string
      expiresAt: string
    }) =>
      apiFetch<LicenseItem>(`/v1/lms/licenses/${licenseId}/renew`, {
        method: "POST",
        body: JSON.stringify({ expires_at: `${expiresAt}T00:00:00Z` }),
      }),
    onSuccess: (data) => {
      toast({
        title: "Licencja została odnowiona",
        description: data.expires_at,
        status: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "licenses", "customer", customerId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się odnowić licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const issueMutation = useMutation({
    mutationFn: (licenseId: string) =>
      apiFetch<{
        license_jwt: string
        jti: string
        kid: string
        expires_at: string
      }>(`/v1/lms/licenses/${licenseId}/issue`, {
        method: "POST",
      }),
    onSuccess: (data) => {
      setIssuedToken({
        token: data.license_jwt,
        jti: data.jti,
        kid: data.kid,
        expiresAt: data.expires_at,
      })
      toast({
        title: "Licencja została wystawiona",
        description: `jti: ${data.jti}`,
        status: "success",
        duration: 3000,
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się wystawić licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (licenseId: string) =>
      apiFetch<{ status: string }>(`/v1/lms/licenses/${licenseId}/revoke`, {
        method: "POST",
        body: JSON.stringify({ reason: "manual_admin_revoke" }),
      }),
    onSuccess: () => {
      revokeConfirm.onClose()
      setIssuedToken(null)
      toast({
        title: "Licencja została unieważniona",
        status: "warning",
        duration: 3000,
      })
      queryClient.invalidateQueries({
        queryKey: ["lms", "licenses", "customer", customerId],
      })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się unieważnić licencji",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  if (customerQuery.isLoading) {
    return (
      <Container maxW="7xl" py={6}>
        <Flex minH="240px" align="center" justify="center" gap={3}>
          <Spinner />
          <Text>Ładowanie karty klienta...</Text>
        </Flex>
      </Container>
    )
  }

  if (customerQuery.isError || !customerQuery.data) {
    return (
      <Container maxW="5xl" py={6}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          Nie udało się załadować klienta.
        </Alert>
      </Container>
    )
  }

  const customer = customerQuery.data

  return (
    <Container maxW="9xl" py={6}>
      <Stack spacing={5}>
        <HStack justify="space-between" align="center" flexWrap="wrap">
          <HStack spacing={3} align="center">
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="outline"
              onClick={() => navigate({ to: "/admin/customers" })}
            >
              Wróć do listy
            </Button>
            <Box>
              <Heading size="lg">
                {customer.name || customer.code || customer.customer_id}
              </Heading>
              <HStack mt={1} spacing={2} flexWrap="wrap">
                <Badge colorScheme={getCustomerStatusColor(customer.status)}>
                  {customer.status || "unknown"}
                </Badge>
                <Text color={mutedTextColor} fontSize="sm">
                  {customer.code || "Brak kodu"} • {customer.customer_id}
                </Text>
              </HStack>
            </Box>
          </HStack>

          <Button
            rightIcon={<ExternalLinkIcon />}
            variant="outline"
            onClick={() =>
              navigate({
                to: "/admin/licenses",
              })
            }
          >
            Otwórz rejestr licencji
          </Button>
        </HStack>

        <Grid templateColumns={{ base: "1fr", xl: "380px 1fr" }} gap={5}>
          <VStack align="stretch" spacing={5}>
            <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
              <Heading size="md" mb={4}>
                Dane klienta
              </Heading>
              <Stack spacing={3}>
                <Box>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Nazwa
                  </Text>
                  <Text fontWeight="semibold">{customer.name || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Email
                  </Text>
                  <Text>{customer.email || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Telefon
                  </Text>
                  <Text>{customer.phone || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Country
                  </Text>
                  <Text>{customer.country_code || "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedTextColor}>
                    Customer ID
                  </Text>
                  <Text fontFamily="mono" fontSize="sm">
                    {customer.customer_id}
                  </Text>
                </Box>
              </Stack>
            </Box>

            <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
              <Heading size="md" mb={4}>
                Edycja klienta
              </Heading>
              {customerValidationError ? (
                <Alert status="error" borderRadius="lg" mb={4}>
                  <AlertIcon />
                  {customerValidationError}
                </Alert>
              ) : null}
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Kod
                    </FormLabel>
                    <Input
                      minW={0}
                      value={customerForm.code}
                      onChange={(e) =>
                        setCustomerForm((current) => ({
                          ...current,
                          code: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Status
                    </FormLabel>
                    <Select
                      value={customerForm.status}
                      onChange={(e) =>
                        setCustomerForm((current) => ({
                          ...current,
                          status: e.target.value,
                        }))
                      }
                    >
                      <option value="active">aktywny</option>
                      <option value="suspended">zawieszony</option>
                      <option value="closed">zamknięty</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={2}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Nazwa
                    </FormLabel>
                    <Input
                      minW={0}
                      value={customerForm.name}
                      onChange={(e) =>
                        setCustomerForm((current) => ({
                          ...current,
                          name: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={2}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      E-mail
                    </FormLabel>
                    <Input
                      minW={0}
                      value={customerForm.email}
                      onChange={(e) =>
                        setCustomerForm((current) => ({
                          ...current,
                          email: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Telefon
                    </FormLabel>
                    <Input
                      minW={0}
                      value={customerForm.phone}
                      onChange={(e) =>
                        setCustomerForm((current) => ({
                          ...current,
                          phone: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={1}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Kraj
                    </FormLabel>
                    <Input
                      minW={0}
                      value={customerForm.country_code}
                      onChange={(e) =>
                        setCustomerForm((current) => ({
                          ...current,
                          country_code: e.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
              </Grid>
              <HStack mt={4}>
                <Button
                  colorScheme="blue"
                  isLoading={customerUpdateMutation.isPending}
                  onClick={() => {
                    setCustomerValidationError(null)
                    customerUpdateMutation.mutate(customerForm)
                  }}
                >
                  Zapisz klienta
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setCustomerValidationError(null)
                    setCustomerForm({
                      customer_id: customer.customer_id,
                      code: customer.code || "",
                      name: customer.name || "",
                      email: customer.email || "",
                      phone: customer.phone || "",
                      country_code: customer.country_code || "",
                      status: customer.status || "active",
                    })
                  }}
                >
                  Resetuj
                </Button>
              </HStack>
            </Box>

            <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
              <Heading size="md" mb={4}>
                Integracja GUS
              </Heading>
              {gusStatusQuery.isLoading ? (
                <Flex minH="120px" align="center" justify="center" gap={3}>
                  <Spinner size="sm" />
                  <Text>Sprawdzanie statusu GUS...</Text>
                </Flex>
              ) : gusStatusQuery.isError || !gusStatusQuery.data ? (
                <Alert status="error" borderRadius="lg">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="semibold">
                      Nie udało się pobrać statusu integracji GUS.
                    </Text>
                    <Button
                      mt={3}
                      size="sm"
                      variant="outline"
                      onClick={() => gusStatusQuery.refetch()}
                    >
                      Spróbuj ponownie
                    </Button>
                  </Box>
                </Alert>
              ) : (
                <Stack spacing={3}>
                  <HStack
                    justify="space-between"
                    align="center"
                    flexWrap="wrap"
                  >
                    <Badge
                      colorScheme={getGUSStatusColor(
                        gusStatusQuery.data.status,
                      )}
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {getGUSStatusLabel(gusStatusQuery.data.status)}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => gusStatusQuery.refetch()}
                      isLoading={gusStatusQuery.isFetching}
                    >
                      Sprawdź ponownie
                    </Button>
                  </HStack>

                  <Text>{gusStatusQuery.data.message}</Text>

                  <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                    <Box borderWidth="1px" borderRadius="lg" p={3}>
                      <Text fontSize="sm" color={mutedTextColor}>
                        Provider
                      </Text>
                      <Text fontWeight="semibold" textTransform="uppercase">
                        {gusStatusQuery.data.provider_mode}
                      </Text>
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={3}>
                      <Text fontSize="sm" color={mutedTextColor}>
                        Runtime lookup
                      </Text>
                      <Text fontWeight="semibold">
                        {gusStatusQuery.data.can_lookup
                          ? "Dostępny"
                          : "Niedostępny"}
                      </Text>
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={3}>
                      <Text fontSize="sm" color={mutedTextColor}>
                        API key GUS
                      </Text>
                      <Text fontWeight="semibold">
                        {gusStatusQuery.data.api_key_configured
                          ? "Skonfigurowany"
                          : "Brak / niepotrzebny w mock"}
                      </Text>
                    </Box>
                    <Box borderWidth="1px" borderRadius="lg" p={3}>
                      <Text fontSize="sm" color={mutedTextColor}>
                        Ostatnia kontrola
                      </Text>
                      <Text fontWeight="semibold">
                        {new Date(
                          gusStatusQuery.data.checked_at,
                        ).toLocaleString()}
                      </Text>
                    </Box>
                  </Grid>

                  <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <Text fontSize="sm" color={mutedTextColor}>
                      Publiczny endpoint runtime
                    </Text>
                    <Text fontFamily="mono" fontSize="sm">
                      {gusStatusQuery.data.public_lookup_path}
                    </Text>
                  </Box>

                  {(gusStatusQuery.data.todo ?? []).length > 0 ? (
                    <Alert
                      status="warning"
                      borderRadius="lg"
                      alignItems="start"
                    >
                      <AlertIcon mt={1} />
                      <Box>
                        <Text fontWeight="semibold" mb={2}>
                          TODO do przejścia na realny GUS
                        </Text>
                        <Stack spacing={1}>
                          {(gusStatusQuery.data.todo ?? []).map((item) => (
                            <Text key={item} fontSize="sm">
                              - {item}
                            </Text>
                          ))}
                        </Stack>
                      </Box>
                    </Alert>
                  ) : null}
                </Stack>
              )}
            </Box>

            <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
              <Heading size="md" mb={4}>
                Przegląd licencji
              </Heading>
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={3}>
                <Box borderWidth="1px" borderRadius="lg" p={3}>
                  <Stat>
                    <StatLabel>Łącznie licencji</StatLabel>
                    <StatNumber>{licensesQuery.data?.total ?? 0}</StatNumber>
                    <StatHelpText>Dla tego klienta</StatHelpText>
                  </Stat>
                </Box>
                <Box borderWidth="1px" borderRadius="lg" p={3}>
                  <Stat>
                    <StatLabel>Aktywne</StatLabel>
                    <StatNumber>
                      {
                        (licensesQuery.data?.items ?? []).filter(
                          (item) => item.status === "active",
                        ).length
                      }
                    </StatNumber>
                    <StatHelpText>
                      Gotowe do synchronizacji runtime
                    </StatHelpText>
                  </Stat>
                </Box>
              </Grid>
            </Box>
          </VStack>

          <VStack align="stretch" spacing={5}>
            <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
              <HStack
                justify="space-between"
                align="center"
                mb={4}
                flexWrap="wrap"
              >
                <Box>
                  <Heading size="md">Licencje klienta</Heading>
                  <Text color={mutedTextColor} mt={1}>
                    Kliknięcie w wiersz wybiera licencję do edycji poniżej.
                  </Text>
                </Box>
                <Button onClick={() => resetFormForNewLicense()}>
                  Nowa licencja
                </Button>
              </HStack>

              <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
                <TableContainer>
                  <Table size="sm">
                    <Thead bg={tableHeadBg}>
                      <Tr>
                        <Th>ID licencji</Th>
                        <Th>Produkt</Th>
                        <Th>Plan</Th>
                        <Th>Status</Th>
                        <Th>Wygasa</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {licensesQuery.isLoading ? (
                        <Tr>
                          <Td colSpan={6}>
                            <Spinner size="sm" />
                          </Td>
                        </Tr>
                      ) : (licensesQuery.data?.items ?? []).length === 0 ? (
                        <Tr>
                          <Td
                            colSpan={6}
                            textAlign="center"
                            py={6}
                            color={mutedTextColor}
                          >
                            Ten klient nie ma jeszcze licencji.
                          </Td>
                        </Tr>
                      ) : (
                        (licensesQuery.data?.items ?? []).map((item) => (
                          <Tr
                            key={item.id}
                            cursor="pointer"
                            bg={
                              item.license_id === selectedLicenseId
                                ? tableRowHoverBg
                                : undefined
                            }
                            _hover={{ bg: tableRowHoverBg }}
                            onClick={() => {
                              setSelectedLicenseId(item.license_id)
                              setIssuedToken(null)
                              setValidationError(null)
                              setForm(buildUpsertPayloadFromLicense(item))
                              setRenewDate(toDateInputValue(item.expires_at))
                            }}
                          >
                            <Td fontFamily="mono" fontSize="xs">
                              {item.license_id}
                            </Td>
                            <Td textTransform="uppercase">{item.product}</Td>
                            <Td>{item.plan}</Td>
                            <Td>
                              <Badge
                                colorScheme={getLicenseStatusColor(item.status)}
                              >
                                {item.status}
                              </Badge>
                            </Td>
                            <Td>{toDateInputValue(item.expires_at) || "-"}</Td>
                            <Td>
                              <Link
                                onClick={(event) => {
                                  event.stopPropagation()
                                  navigate({ to: "/admin/licenses" })
                                }}
                              >
                                Szczegóły
                              </Link>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
              <HStack
                justify="space-between"
                align="center"
                mb={4}
                flexWrap="wrap"
              >
                <Box>
                  <Heading size="md">
                    {selectedLicenseId
                      ? "Edycja licencji"
                      : "Tworzenie licencji"}
                  </Heading>
                  <Text color={mutedTextColor} mt={1}>
                    Zarządzaj licencją bezpośrednio z karty klienta.
                  </Text>
                </Box>
                {selectedLicenseId ? (
                  <HStack>
                    <Button
                      size="sm"
                      onClick={() =>
                        quickPlanMutation.mutate({
                          licenseId: selectedLicenseId,
                          plan: "BASIC",
                          expectedRevision:
                            selectedLicense?.license_revision ?? 0,
                        })
                      }
                      isLoading={quickPlanMutation.isPending}
                    >
                      BASIC
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={() =>
                        quickPlanMutation.mutate({
                          licenseId: selectedLicenseId,
                          plan: "PRO",
                          expectedRevision:
                            selectedLicense?.license_revision ?? 0,
                        })
                      }
                      isLoading={quickPlanMutation.isPending}
                    >
                      PRO
                    </Button>
                  </HStack>
                ) : null}
              </HStack>

              {validationError ? (
                <Alert status="error" borderRadius="lg" mb={4}>
                  <AlertIcon />
                  {validationError}
                </Alert>
              ) : null}

              <Grid
                templateColumns={{
                  base: "1fr",
                  lg: "repeat(12, minmax(0, 1fr))",
                }}
                gap={3}
              >
                <GridItem colSpan={{ base: 1, lg: 6 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      License ID
                    </FormLabel>
                    <Input
                      value={form.license_id || "Auto-generated by backend"}
                      isReadOnly
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Product
                    </FormLabel>
                    <Select
                      value={form.product}
                      onChange={(event) =>
                        applyProductDefaults(
                          event.target.value as RuntimeTarget,
                        )
                      }
                    >
                      <option value="wms">wms</option>
                      <option value="hrm">hrm</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Status
                    </FormLabel>
                    <Select
                      value={form.status}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          status: event.target.value,
                        }))
                      }
                    >
                      {licenseOptions.statuses.map((statusOption) => (
                        <option key={statusOption} value={statusOption}>
                          {statusOption}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Plan
                    </FormLabel>
                    <Select
                      value={form.plan}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          plan: event.target.value,
                        }))
                      }
                    >
                      <option value="BASIC">BASIC</option>
                      <option value="PRO">PRO</option>
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Starts at
                    </FormLabel>
                    <Input
                      type="date"
                      value={form.starts_at}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          starts_at: event.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 3 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Expires at
                    </FormLabel>
                    <Input
                      type="date"
                      value={form.expires_at}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          expires_at: event.target.value,
                        }))
                      }
                    />
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 2 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Grace days
                    </FormLabel>
                    <NumberInput
                      min={1}
                      value={form.grace_days}
                      onChange={(_, valueAsNumber) =>
                        setForm((current) => ({
                          ...current,
                          grace_days: Number.isFinite(valueAsNumber)
                            ? valueAsNumber
                            : current.grace_days,
                        }))
                      }
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 2 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Offline max days
                    </FormLabel>
                    <NumberInput
                      min={1}
                      value={form.offline_max_days}
                      onChange={(_, valueAsNumber) =>
                        setForm((current) => ({
                          ...current,
                          offline_max_days: Number.isFinite(valueAsNumber)
                            ? valueAsNumber
                            : current.offline_max_days,
                        }))
                      }
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 2 }}>
                  <FormControl>
                    <FormLabel mb={1} fontSize="sm">
                      Critical after limited
                    </FormLabel>
                    <NumberInput
                      min={1}
                      value={form.critical_after_limited_days}
                      onChange={(_, valueAsNumber) =>
                        setForm((current) => ({
                          ...current,
                          critical_after_limited_days: Number.isFinite(
                            valueAsNumber,
                          )
                            ? valueAsNumber
                            : current.critical_after_limited_days,
                        }))
                      }
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 12 }}>
                  <Heading size="sm" mt={2} mb={2}>
                    Модулі ліцензії
                  </Heading>
                  {form.product === "wms" ? (
                    <Text color={mutedTextColor} fontSize="sm" mb={3}>
                      BASIC містить увесь функціонал, крім виробничого контуру.
                      Окремі вимкнення зберігаються при зміні плану; у WMS вони
                      застосуються після синхронізації.
                    </Text>
                  ) : null}
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      lg: "repeat(2, minmax(0, 1fr))",
                    }}
                    gap={3}
                  >
                    {currentProductContract.feature_definitions.length === 0 ? (
                      <Text color={mutedTextColor}>
                        Dla tego produktu nie ma konfigurowalnych flag funkcji.
                      </Text>
                    ) : (
                      currentProductContract.feature_definitions.map(
                        (feature) => (
                          <Box
                            key={feature.key}
                            borderWidth="1px"
                            borderRadius="lg"
                            p={3}
                          >
                            <HStack justify="space-between" align="start">
                              <Box>
                                <Text fontWeight="semibold">
                                  {feature.label}
                                </Text>
                                {feature.description ? (
                                  <Text color={mutedTextColor} fontSize="sm">
                                    {feature.description}
                                  </Text>
                                ) : null}
                                {form.product === "wms" &&
                                !(form.plan === "BASIC"
                                  ? feature.basic_included
                                  : feature.pro_included) ? (
                                  <Text color="orange.400" fontSize="xs">
                                    Доступно лише у PRO.
                                  </Text>
                                ) : form.product === "wms" &&
                                  form.features[feature.key] === false ? (
                                  <Text color="orange.400" fontSize="xs">
                                    Вимкнено для цієї ліцензії.
                                  </Text>
                                ) : null}
                              </Box>
                              <Select
                                maxW="110px"
                                isDisabled={
                                  feature.editable === false ||
                                  (form.product === "wms" &&
                                    !(form.plan === "BASIC"
                                      ? feature.basic_included
                                      : feature.pro_included))
                                }
                                value={String(
                                  Boolean(form.features[feature.key]),
                                )}
                                onChange={(event) =>
                                  setForm((current) => {
                                    const enabled =
                                      event.target.value === "true"
                                    const moduleOverrides = {
                                      ...current.module_overrides,
                                    }
                                    if (enabled)
                                      delete moduleOverrides[feature.key]
                                    else moduleOverrides[feature.key] = false
                                    return {
                                      ...current,
                                      module_overrides: moduleOverrides,
                                      features: {
                                        ...current.features,
                                        [feature.key]: enabled,
                                      },
                                    }
                                  })
                                }
                              >
                                <option value="true">on</option>
                                <option value="false">off</option>
                              </Select>
                              {form.product === "wms" &&
                              !(form.plan === "BASIC"
                                ? feature.basic_included
                                : feature.pro_included) ? (
                                <Text fontSize="xs" color="orange.400">
                                  Dostępne wyłącznie w planie PRO.
                                </Text>
                              ) : form.product === "wms" &&
                                feature.dependencies?.length ? (
                                <Text fontSize="xs" color={mutedTextColor}>
                                  Zależne od: {feature.dependencies.join(", ")}.
                                </Text>
                              ) : null}
                            </HStack>
                          </Box>
                        ),
                      )
                    )}
                  </Grid>
                </GridItem>
                <GridItem colSpan={{ base: 1, lg: 12 }}>
                  <Heading size="sm" mt={2} mb={2}>
                    Limits
                  </Heading>
                  <Grid
                    templateColumns={{
                      base: "1fr",
                      lg: "repeat(3, minmax(0, 1fr))",
                    }}
                    gap={3}
                  >
                    {currentProductContract.limit_definitions.length === 0 ? (
                      <Text color={mutedTextColor}>
                        Dla tego produktu nie ma limitów liczbowych.
                      </Text>
                    ) : (
                      currentProductContract.limit_definitions.map((limit) => (
                        <FormControl key={limit.key}>
                          <FormLabel mb={1} fontSize="sm">
                            {limit.label}
                          </FormLabel>
                          <NumberInput
                            min={limit.min_value}
                            value={
                              form.limits[limit.key] ?? limit.default_value
                            }
                            onChange={(_, valueAsNumber) =>
                              setForm((current) => ({
                                ...current,
                                limits: {
                                  ...current.limits,
                                  [limit.key]: Number.isFinite(valueAsNumber)
                                    ? valueAsNumber
                                    : (current.limits[limit.key] ??
                                      limit.default_value),
                                },
                              }))
                            }
                          >
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                      ))
                    )}
                  </Grid>
                </GridItem>
              </Grid>

              <HStack mt={4} spacing={3} flexWrap="wrap">
                <Button
                  colorScheme="blue"
                  isLoading={upsertMutation.isPending}
                  isDisabled={
                    form.product === "wms" &&
                    (!optionsQuery.isSuccess ||
                      optionsQuery.isError ||
                      !currentProductContract.modules?.length)
                  }
                  onClick={() => {
                    setValidationError(null)
                    if (!form.customer_id.trim()) {
                      setValidationError("customer_id is required")
                      return
                    }
                    if (!form.starts_at || !form.expires_at) {
                      setValidationError(
                        "starts_at and expires_at are required",
                      )
                      return
                    }
                    upsertMutation.mutate(form)
                  }}
                >
                  Zapisz licencję
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    selectedLicense
                      ? setForm(buildUpsertPayloadFromLicense(selectedLicense))
                      : resetFormForNewLicense(form.product)
                  }
                >
                  Resetuj zmiany
                </Button>
              </HStack>
            </Box>

            {selectedLicenseId ? (
              <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
                <Heading size="md" mb={4}>
                  Operacje na licencji
                </Heading>
                <HStack spacing={3} flexWrap="wrap" mb={4}>
                  <FormControl maxW="240px">
                    <FormLabel mb={1} fontSize="sm">
                      Nowa data wygaśnięcia
                    </FormLabel>
                    <Input
                      type="date"
                      value={renewDate}
                      onChange={(event) => setRenewDate(event.target.value)}
                    />
                  </FormControl>
                  <Button
                    mt={{ base: 0, md: 6 }}
                    onClick={() =>
                      renewDate
                        ? renewMutation.mutate({
                            licenseId: selectedLicenseId,
                            expiresAt: renewDate,
                          })
                        : setValidationError("Wskaż datę odnowienia")
                    }
                    isLoading={renewMutation.isPending}
                  >
                    Renew
                  </Button>
                  <Button
                    mt={{ base: 0, md: 6 }}
                    onClick={() => issueMutation.mutate(selectedLicenseId)}
                    isLoading={issueMutation.isPending}
                  >
                    Issue JWT
                  </Button>
                  <Button
                    mt={{ base: 0, md: 6 }}
                    colorScheme="red"
                    variant="outline"
                    onClick={revokeConfirm.onOpen}
                    isLoading={revokeMutation.isPending}
                  >
                    Revoke
                  </Button>
                </HStack>

                {issuedToken ? (
                  <Alert status="success" borderRadius="lg" alignItems="start">
                    <AlertIcon mt={1} />
                    <Box>
                      <Text fontWeight="semibold">Wystawiono JWT</Text>
                      <Text fontSize="sm">jti: {issuedToken.jti}</Text>
                      <Text fontSize="sm">kid: {issuedToken.kid}</Text>
                      <Text fontSize="sm">
                        expires_at: {issuedToken.expiresAt}
                      </Text>
                      <Box mt={3}>
                        <Text fontSize="sm" color={mutedTextColor} mb={1}>
                          Token
                        </Text>
                        <Input
                          value={issuedToken.token}
                          readOnly
                          fontFamily="mono"
                          fontSize="xs"
                        />
                      </Box>
                    </Box>
                  </Alert>
                ) : null}

                {revokeConfirm.isOpen ? (
                  <Alert status="warning" borderRadius="lg" mt={4}>
                    <AlertIcon />
                    <HStack justify="space-between" w="full" flexWrap="wrap">
                      <Text>
                        Czy potwierdzić unieważnienie dla {selectedLicenseId}?
                      </Text>
                      <HStack>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={revokeConfirm.onClose}
                        >
                          Anuluj
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          onClick={() =>
                            revokeMutation.mutate(selectedLicenseId)
                          }
                        >
                          Potwierdź
                        </Button>
                      </HStack>
                    </HStack>
                  </Alert>
                ) : null}
              </Box>
            ) : null}
          </VStack>
        </Grid>
      </Stack>
    </Container>
  )
}
